import { FC, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, CircularProgress, Typography } from "@mui/material";
import { LibraryDocument, LibraryField } from "../templateLibrary.types";
import templateLibraryStyles from "../TemplateLibrary.styles";

// Same worker the app's other react-pdf viewers use.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const styles = templateLibraryStyles;

/** Render width; CSS scales the canvas down to the column. */
const PAGE_WIDTH = 640;

const PageLoading = () => (
  <Box sx={styles.pageLoading} role="status" aria-label="Loading document">
    <CircularProgress size={26} />
  </Box>
);

const Unavailable = () => (
  <Box sx={styles.pageLoading}>
    <Typography sx={styles.previewUnavailable}>
      Preview not available.
    </Typography>
  </Box>
);

/** "date_signed" → "Date signed". */
const fieldTypeLabel = (type: string) => {
  const words = (type || "field").replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Tick boxes are too small for a caption. */
const SMALL_FIELD_TYPES = ["checkbox", "radio_button"];

/**
 * The fields the author placed on one page, drawn as boxes at their saved
 * position (percentages of the page), labelled "Signature · Employer".
 */
const FieldBoxes = ({
  fields,
  roles,
}: {
  fields: LibraryField[];
  roles: string[];
}) => (
  <>
    {fields.map((field, i) => {
      const roleIndex = field.signerRole ? roles.indexOf(field.signerRole) : -1;
      const tone = (roleIndex === 0 ? 0 : roleIndex === 1 ? 1 : 2) as 0 | 1 | 2;
      const caption = SMALL_FIELD_TYPES.includes(field.type)
        ? ""
        : [fieldTypeLabel(field.type), field.signerRole]
            .filter(Boolean)
            .join(" · ");
      return (
        <Box
          key={i}
          aria-hidden="true"
          title={caption}
          sx={[
            styles.fieldBox(tone),
            {
              left: `${field.x}%`,
              top: `${field.y}%`,
              width: `${field.width}%`,
              height: `${field.height}%`,
            },
          ]}
        >
          {caption && (
            <Box component="span" sx={styles.fieldCaption}>
              {caption}
            </Box>
          )}
        </Box>
      );
    })}
  </>
);

/** Every page of one library document, stacked, read-only, with its fields. */
export const LibraryDocumentPreview: FC<{ document: LibraryDocument }> = ({
  document,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [failed, setFailed] = useState(false);

  if (!document.url || failed) return <Unavailable />;

  return (
    <Box sx={{ width: "100%", maxWidth: PAGE_WIDTH, flexShrink: 0 }}>
      <Document
        key={document.url}
        file={document.url}
        loading={<PageLoading />}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={() => setFailed(true)}
        renderMode="canvas"
      >
        {Array.from({ length: numPages }).map((_, i) => (
          <Box
            key={i}
            sx={[styles.page, { mb: i < numPages - 1 ? "22px" : 0 }]}
          >
            <Box sx={styles.pageCanvas}>
              <Page
                pageNumber={i + 1}
                width={PAGE_WIDTH}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={<PageLoading />}
                canvasRef={(node: HTMLCanvasElement | null) => {
                  if (node) {
                    node.setAttribute("role", "img");
                    node.setAttribute(
                      "aria-label",
                      `${document.name}, page ${i + 1} of ${numPages}`,
                    );
                  }
                }}
              />
              <FieldBoxes
                fields={(document.fields ?? []).filter((f) => f.page === i)}
                roles={document.signerRoles ?? []}
              />
            </Box>
          </Box>
        ))}
      </Document>
    </Box>
  );
};

export default LibraryDocumentPreview;
